import { useContext, useEffect, useState } from "react";
import styles from "./FreeGift.module.css";
import { Button, Checkbox, Collapsible, Icon, InlineError, TextField, Thumbnail } from "@shopify/polaris";
import { CollectionIcon, SearchIcon } from "@shopify/polaris-icons";
import { ProductObj, VariantsObj } from "../../apps/Bundles/Types";
import CloseIcon from "../Icons/CloseIcon";
import { AppConfigContext } from "../../context/AppConfigContext";
import { getProductsDynamicallyAndReturnNewProductsArray } from "../../apps/Bundles/constants";
import { filterFreeGifts } from "./FreeGiftConstants";

type FreeGiftProps = {
    dispatchBundleConfig: any;
    free_gifts: any;
    allow_free_gifts: any;
    free_gift_text: any;
    submit: any;
    excludeProducts: ProductObj[];
};

const FreeGift = ({
    dispatchBundleConfig,
    allow_free_gifts,
    free_gifts,
    free_gift_text,
    submit,
    excludeProducts,
}: FreeGiftProps) => {
    const [expandFreeGiftCollapsible, setExpandFreeGiftCollapsible] = useState(allow_free_gifts.value);

    useEffect(() => {
        if (allow_free_gifts?.value !== undefined) {
            setExpandFreeGiftCollapsible(allow_free_gifts.value);
        }
    }, [allow_free_gifts?.value]);

    useEffect(() => {
        const data = filterFreeGifts(free_gifts.value, excludeProducts);
        if (data?.isChanged) {
            dispatchBundleConfig({
                payload: data.arr,
                key: "free_gifts",
            });
            free_gifts.onChange(data.arr);
        }
    }, [excludeProducts]);

    const [productSearchQuery, setProductSearchQuery] = useState("");

    const context = useContext(AppConfigContext);
    const { sft } = context;

    const handleProductSearch = async () => {
        const preSelected = free_gifts.value.map((product: ProductObj) => ({
            id: product?.id,
            variants: product?.variants.map((variant: VariantsObj) => ({
                id: variant?.id,
            })),
        }));
        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            query: productSearchQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: false,
            },
        });
        if (selected) {
            const productsArr: ProductObj[] = [];
            selected?.flatMap((product: any) => {
                if (!excludeProducts.find((ep) => ep.id == product?.id)) {
                    productsArr.push({
                        id: product?.id,
                        images: product?.images,
                        title: product?.title,
                        handle: product?.handle,
                        quantity: 1,
                        maxProductStock: product?.totalInventory,
                        selectedVariants: [],
                        subscriptions: [],
                        oneTimePurchase: true,
                        isSubscribed: false,
                        variants: product?.variants?.map((variant: VariantsObj) => ({
                            image: { ...variant.image },
                            id: variant?.id,
                            displayName: variant.displayName,
                            title: variant?.title?.toLowerCase() == "default title" ? product?.title : variant?.title,
                            price: variant?.price,
                            quantity: 1,
                            selectedOptions: variant?.selectedOptions,
                            copyPrice: variant.price,
                            compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice : variant.price,
                        })),
                        options: product?.options,
                    });
                } else {
                    shopify.toast.show("Included product cannot be added as free gift(s)", { isError: true });
                }
            });

            dispatchBundleConfig({
                payload: productsArr,
                key: "free_gifts",
            });
            free_gifts.onChange(productsArr);
            setProductSearchQuery("");

            try {
                const newProductsArr = await getProductsDynamicallyAndReturnNewProductsArray(productsArr, sft);
                dispatchBundleConfig({
                    payload: newProductsArr,
                    key: "free_gifts",
                });
                free_gifts.onChange(newProductsArr);
            } catch (error) {
                console._log(error);
                dispatchBundleConfig({
                    payload: productsArr,
                    key: "free_gifts",
                });
                free_gifts.onChange(productsArr);
            }
        }
    };

    const removeProducts = (pId: string = "") => {
        const temp = [...free_gifts.value];
        let idx: number | undefined = -1;
        idx = temp.findIndex((p: any) => p.id == pId);
        if (idx >= 0) {
            temp.splice(idx, 1);
            dispatchBundleConfig({
                payload: temp,
                key: "free_gifts",
            });
            free_gifts.onChange(temp);
        }
    };

    const handleQuantity = (pId = "", val: number) => {
        const temp: ProductObj[] = [...free_gifts.value];
        let idx: number | undefined = -1;

        idx = temp.findIndex((p) => p.id == pId);

        if (idx >= 0) {
            temp[idx] = {
                ...temp[idx],
                quantity: val,
            };
        }

        dispatchBundleConfig({
            payload: temp,
            key: "free_gifts",
        });
        free_gifts.onChange(temp);
    };

    const [firstRender, setFirstRender] = useState(true);

    useEffect(() => {
        if (firstRender) {
            setFirstRender(false);
            return;
        }

        if (free_gifts?.value?.length) {
            free_gifts?.runValidation();
        }

        if (!allow_free_gifts.value) {
            allow_free_gifts?.runValidation();
        }

        return () => {
            if (free_gifts?.value?.length && allow_free_gifts.value) {
                free_gifts?.runValidation();
                allow_free_gifts?.runValidation();
            }
        };
    }, [free_gifts.value, allow_free_gifts.value]);

    return (
        <div>
            <Checkbox
                label={"Add Free Gift With This Bundle"}
                {...allow_free_gifts}
                value=""
                checked={allow_free_gifts.value}
                onChange={(val) => {
                    if (val == false) {
                        dispatchBundleConfig({
                            payload: [],
                            key: "free_gifts",
                        });
                        free_gifts.onChange([]);
                    }
                    dispatchBundleConfig({
                        payload: val,
                        key: "allow_free_gifts",
                    });
                    allow_free_gifts.onChange(val);
                    setExpandFreeGiftCollapsible(val);
                }}
                onBlur={allow_free_gifts?.onBlur}
            />

            <Collapsible
                open={expandFreeGiftCollapsible}
                id="free-gift-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <div className={styles.tier_gift_wrapper}>
                    <div className={styles.gift_container}>
                        <h5>Free Gift</h5>

                        <TextField
                            label={"Search Product"}
                            labelHidden
                            placeholder="Search Products"
                            autoComplete={"off"}
                            value={productSearchQuery}
                            onChange={(val) => {
                                setProductSearchQuery(val);
                            }}
                            prefix={<Icon source={SearchIcon} />}
                            size="slim"
                            connectedRight={
                                <div style={{ marginLeft: "8px" }}>
                                    <Button onClick={() => handleProductSearch()} size="slim">
                                        Browse
                                    </Button>
                                </div>
                            }
                            error={free_gifts?.error}
                        />

                        {free_gifts?.value?.length > 0 && (
                            <div className={styles.renderFreeGiftFormSide}>
                                {free_gifts.value?.map((p: any, index: number) => (
                                    <div>
                                        <div className={styles.container}>
                                            <div className={styles.container_wrapper}>
                                                <div className={styles.img_container}>
                                                    <Thumbnail
                                                        source={p?.images[0]?.originalSrc || CollectionIcon}
                                                        alt={p?.title}
                                                        size={"small"}
                                                    />
                                                </div>
                                                <p>{p?.title}</p>
                                            </div>

                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: "10px",
                                                    alignItems: "center",
                                                    maxWidth: "130px",
                                                    width: "130px",
                                                }}
                                            >
                                                <TextField
                                                    label={"Quantity"}
                                                    labelHidden
                                                    type="number"
                                                    size="slim"
                                                    min={1}
                                                    placeholder="Quantity"
                                                    autoComplete={"off"}
                                                    value={p.quantity.toString()}
                                                    suffix={"Qty"}
                                                    onBlur={() => {
                                                        if (Number(p.quantity) < 1) {
                                                            const temp = [...free_gifts.value];
                                                            temp[index].quantity = 1;

                                                            dispatchBundleConfig({
                                                                payload: temp,
                                                                key: "free_gifts",
                                                            });
                                                            free_gifts.onChange(temp);
                                                        }
                                                    }}
                                                    onChange={(val) => {
                                                        handleQuantity(p.id, +val);
                                                        // const temp = [
                                                        //     ...free_gifts.value,
                                                        // ];
                                                        // temp[
                                                        //     index
                                                        // ].quantity =
                                                        //     Number(val);

                                                        // dispatchBundleConfig(
                                                        //     {
                                                        //         payload:
                                                        //             temp,
                                                        //         key: "free_gifts",
                                                        //     },
                                                        // );
                                                        // free_gifts.onChange(
                                                        //     temp,
                                                        // );
                                                    }}
                                                />

                                                <div
                                                    onClick={() => {
                                                        removeProducts(p.id);
                                                    }}
                                                    className={styles.closeIcon}
                                                >
                                                    <CloseIcon />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <TextField
                            label={"Free Gift Text"}
                            placeholder="Free Gift Text"
                            autoComplete={"off"}
                            {...free_gift_text}
                            onChange={(val) => {
                                dispatchBundleConfig({
                                    payload: val,
                                    key: "free_gift_text",
                                });
                                free_gift_text.onChange(val);
                            }}
                            onBlur={free_gift_text?.onBlur}
                            maxLength={30}
                            showCharacterCount
                        />
                    </div>
                </div>
            </Collapsible>
        </div>
    );
};

export default FreeGift;
